import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "./Navbar.css";
import API_URL from "./config";
import basketballLogo from "./basketball-logo.png";

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loadingNextGame, setLoadingNextGame] = useState(false);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [hasActiveSave, setHasActiveSave] = useState(false);

  // Check for active save on mount and when activeSaveId changes
  useEffect(() => {
    const activeSaveId = localStorage.getItem("activeSaveId");
    setHasActiveSave(!!activeSaveId);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("access_token");
    localStorage.removeItem("activeSaveId");
    navigate("/");
  };

  const handleViewSchedule = async () => {
    setLoadingSchedule(true);
    try {
      const activeSaveId = localStorage.getItem("activeSaveId");
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user._id;

      if (!activeSaveId) {
        alert("No active save file. Please select or create a save.");
        setLoadingSchedule(false);
        return;
      }

      // Fetch save to get team ID
      const saveRes = await fetch(`${API_URL}/api/saves/${activeSaveId}`, {
        headers: { "X-User-ID": userId },
      });

      if (!saveRes.ok) {
        alert("Failed to load save file");
        setLoadingSchedule(false);
        return;
      }

      const saveData = await saveRes.json();
      const teamId = saveData.save.teamId;

      // Navigate to team schedule
      navigate(`/teams/${teamId}/schedule`);
    } catch (err) {
      alert("Error loading schedule: " + err.message);
    } finally {
      setLoadingSchedule(false);
    }
  };

  const handlePlayNextGame = async () => {
    setLoadingNextGame(true);
    try {
      const activeSaveId = localStorage.getItem("activeSaveId");
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user._id;

      if (!activeSaveId) {
        alert("No active save file. Please select or create a save.");
        setLoadingNextGame(false);
        return;
      }

      // If there's an unfinished game in progress, resume it instead.
      try {
        const activeRes = await fetch(
          `${API_URL}/api/saves/${activeSaveId}/active-live-game`,
          { headers: { "X-User-ID": userId } }
        );
        if (activeRes.ok) {
          const activeData = await activeRes.json();
          if (activeData.active) {
            setLoadingNextGame(false);
            navigate("/live-game", { state: { resume: true } });
            return;
          }
        }
      } catch (err) {
        // Non-fatal — fall through
      }

      // Fetch save to get team ID
      const saveRes = await fetch(`${API_URL}/api/saves/${activeSaveId}`, {
        headers: { "X-User-ID": userId },
      });

      if (!saveRes.ok) {
        alert("Failed to load save file");
        setLoadingNextGame(false);
        return;
      }

      const saveData = await saveRes.json();
      const teamId = saveData.save.teamId;

      // Fetch team schedule
      const scheduleRes = await fetch(
        `${API_URL}/api/teams/${teamId}/schedule?save_id=${activeSaveId}`
      );

      if (!scheduleRes.ok) {
        alert("Failed to load schedule");
        setLoadingNextGame(false);
        return;
      }

      const scheduleData = await scheduleRes.json();
      const games = scheduleData.games || [];

      // Find first unplayed game
      const nextGame = games.find((g) => !g.played);

      if (!nextGame) {
        alert("No upcoming games available");
        setLoadingNextGame(false);
        return;
      }

      // Navigate to play game with the next game
      navigate("/play-game", { state: { game: nextGame } });
    } catch (err) {
      alert("Error loading next game: " + err.message);
    } finally {
      setLoadingNextGame(false);
    }
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <div className="navbar-brand">
          <img src={basketballLogo} alt="Basketball" className="navbar-logo" />
          <h1>Courtside</h1>
        </div>
        
        <div className="navbar-links">
          <Link
            to="/dashboard"
            className={`nav-item ${isActive("/dashboard") ? "active" : ""}`}
          >
            Home
          </Link>
          <Link
            to="/saves"
            className={`nav-item ${isActive("/saves") ? "active" : ""}`}
          >
            My Saves
          </Link>
          <Link
            to="/my-team"
            className={`nav-item ${isActive("/my-team") ? "active" : ""} ${!hasActiveSave ? "disabled" : ""}`}
            onClick={(e) => {
              if (!hasActiveSave) {
                e.preventDefault();
              }
            }}
          >
            My Team
          </Link>
          <button
            onClick={handleViewSchedule}
            className={`nav-item ${location.pathname.includes("/schedule") ? "active" : ""} ${!hasActiveSave ? "disabled" : ""}`}
            disabled={loadingSchedule || !hasActiveSave}
          >
            {loadingSchedule ? "Loading..." : "Schedule"}
          </button>
          <Link
            to="/teams"
            className={`nav-item ${isActive("/teams") && !location.pathname.includes("/schedule") ? "active" : ""} ${!hasActiveSave ? "disabled" : ""}`}
            onClick={(e) => {
              if (!hasActiveSave) {
                e.preventDefault();
              }
            }}
          >
            Teams
          </Link>
          <Link
            to="/standings"
            className={`nav-item ${isActive("/standings") ? "active" : ""} ${!hasActiveSave ? "disabled" : ""}`}
            onClick={(e) => {
              if (!hasActiveSave) {
                e.preventDefault();
              }
            }}
          >
            Standings
          </Link>
          <Link
            to="/league-leaders"
            className={`nav-item ${isActive("/league-leaders") ? "active" : ""} ${!hasActiveSave ? "disabled" : ""}`}
            onClick={(e) => {
              if (!hasActiveSave) {
                e.preventDefault();
              }
            }}
          >
            League Leaders
          </Link>
          <button
            onClick={handlePlayNextGame}
            className={`nav-item play-btn ${!hasActiveSave ? "disabled" : ""}`}
            disabled={loadingNextGame || !hasActiveSave}
          >
            {loadingNextGame ? "Loading..." : "▶ Play Next Game"}
          </button>
          <Link
            to="/settings"
            className={`nav-item ${isActive("/settings") ? "active" : ""}`}
          >
            ⚙ Settings
          </Link>
          <button onClick={handleLogout} className="nav-item logout-btn">
            Logout
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
